import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { environment as env } from '../../environments/environment';
import { AlertPriority } from './alert-settings.service';
import { CurrentRestaurantService } from './current-restaurant.service';
import { JwtService } from './jwt.service';

/** Mirrors backend `AlertGroup`. */
export enum AlertGroup {
  Stock = 'stock',
  Staff = 'staff',
  Maintenance = 'maintenance',
  Reservation = 'reservation',
}

/** Mirrors backend `AlertKey`. */
export enum AlertKey {
  LowStock = 'lowStock',
  OutOfStock = 'outOfStock',
  RestockSuggestion = 'restockSuggestion',
  PlanningPublished = 'planningPublished',
  PlanningModified = 'planningModified',
  LeaveRequestAnswered = 'leaveRequestAnswered',
  ShiftChanged = 'shiftChanged',
  TaskAssigned = 'taskAssigned',
  EquipmentMaintenance = 'equipmentMaintenance',
  EquipmentMalfunction = 'equipmentMalfunction',
  CleaningSupplyLow = 'cleaningSupplyLow',
  NewReservation = 'newReservation',
  ModifiedReservation = 'modifiedReservation',
  CancelledReservation = 'cancelledReservation',
  ArrivalReminder = 'arrivalReminder',
}

export interface AppNotification {
  _id: string;
  userId: string;
  restaurantId: string;
  group: AlertGroup;
  alertKey: AlertKey;
  priority: AlertPriority;
  titleKey: string;
  params: Record<string, unknown>;
  link?: string;
  entityId?: string;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPage {
  data: AppNotification[];
  total: number;
  unreadCount: number;
}

export interface ListNotificationsOptions {
  group?: AlertGroup;
  unreadOnly?: boolean;
  limit?: number;
  skip?: number;
}

const POLL_INTERVAL_MS = 60_000;
const PANEL_SIZE = 10;

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly jwt = inject(JwtService);
  private readonly currentRestaurant = inject(CurrentRestaurantService);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadCount = signal(0);
  readonly recent = signal<AppNotification[]>([]);
  readonly loading = signal(false);

  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') void this.refresh();
  };

  /** Transport seam: everything above reads signals, so switching to SSE
   *  means replacing this method and leaving the components untouched. */
  startPolling(): void {
    if (this.timer) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisible);
    this.destroyRef.onDestroy(() => this.stopPolling());
  }

  stopPolling(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  async refresh(): Promise<void> {
    const restaurantId = this.currentRestaurant.currentId();
    if (!restaurantId || !this.jwt.isLogged()) {
      this.unreadCount.set(0);
      this.recent.set([]);
      return;
    }

    this.loading.set(true);
    try {
      const page = await firstValueFrom(this.list({ limit: PANEL_SIZE }));
      this.recent.set(page.data);
      this.unreadCount.set(page.unreadCount);
    } catch {
      // A failed poll must stay silent: the next tick retries, and surfacing
      // a toast every 60s on a flaky network would be worse than the gap.
    } finally {
      this.loading.set(false);
    }
  }

  list(options: ListNotificationsOptions = {}): Observable<NotificationPage> {
    const restaurantId = this.currentRestaurant.currentId();
    const params: Record<string, string> = {};
    if (options.group) params['group'] = options.group;
    if (options.unreadOnly) params['unreadOnly'] = 'true';
    if (options.limit != null) params['limit'] = String(options.limit);
    if (options.skip != null) params['skip'] = String(options.skip);

    return this.http.get<NotificationPage>(`${env.apiUrl}/notifications/me/${restaurantId}`, { params });
  }

  async markRead(id: string): Promise<void> {
    await firstValueFrom(this.http.patch(`${env.apiUrl}/notifications/${id}/read`, {}));
    this.applyLocalRead(id);
  }

  async markAllRead(): Promise<void> {
    const restaurantId = this.currentRestaurant.currentId();
    await firstValueFrom(this.http.patch(`${env.apiUrl}/notifications/me/${restaurantId}/read-all`, {}));
    const now = new Date().toISOString();
    this.recent.update((items) => items.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    this.unreadCount.set(0);
  }

  async remove(id: string): Promise<void> {
    await firstValueFrom(this.http.delete(`${env.apiUrl}/notifications/${id}`));
    const removed = this.recent().find((n) => n._id === id);
    this.recent.update((items) => items.filter((n) => n._id !== id));
    if (removed && !removed.readAt) {
      this.unreadCount.update((count) => Math.max(0, count - 1));
    }
  }

  private applyLocalRead(id: string): void {
    const target = this.recent().find((n) => n._id === id);
    if (!target || target.readAt) return;
    const now = new Date().toISOString();
    this.recent.update((items) => items.map((n) => (n._id === id ? { ...n, readAt: now } : n)));
    this.unreadCount.update((count) => Math.max(0, count - 1));
  }
}
